import { useCallback, useRef, useEffect } from "react";
import { useChatControllerMarkAsRead } from "@semo/api-contracts";

interface UseReadReceiptOptions {
  roomId: string;
  currentUserId: string;
  messages: any[];
}

/**
 * Hook to handle read receipt logic (Debounce + API Call)
 * Decouples "How to mark as read" from "When to mark as read"
 */
export function useReadReceipt({
  roomId,
  currentUserId,
  messages,
}: UseReadReceiptOptions) {
  const { mutate: markRoomAsReadMutation } = useChatControllerMarkAsRead();
  const lastMarkedMessageIdRef = useRef<string | null>(null);
  const pendingReadMessageIdRef = useRef<string | null>(null);
  const markReadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (markReadTimeoutRef.current) {
        clearTimeout(markReadTimeoutRef.current);
      }
    };
  }, []);

  const markMessagesAsRead = useCallback(
    (targetMessages?: any[]) => {
      const msgs = targetMessages || messages;
      if (msgs.length === 0) {
        console.log("[ReadReceipt] No messages to mark");
        return;
      }

      // Find the latest message that is NOT sent by me
      // Input msgs are expected to be in display order (Oldest -> Newest)
      // We reverse to find the Newest first
      const lastOtherUserMessage = [...msgs]
        .reverse()
        .find((msg) => msg.senderId !== currentUserId && !msg.isPending);

      if (!lastOtherUserMessage?.id) {
        console.log("[ReadReceipt] No target message found", {
          total: msgs.length,
          lastSender: msgs[msgs.length - 1]?.senderId,
          myId: currentUserId,
        });
        return;
      }

      const lastReadMessageId = lastOtherUserMessage.id;

      // 1. Avoid duplicate calls if already marked on server
      if (lastMarkedMessageIdRef.current === lastReadMessageId) {
        console.log(
          "[ReadReceipt] Already marked this message ID",
          lastReadMessageId,
        );
        return;
      }

      // 2. Stable Debounce:
      // If we are already waiting to mark THIS exact message, let the existing timer run.
      // Do not cancel it, or we risk indefinite postponement (starvation).
      if (
        markReadTimeoutRef.current &&
        pendingReadMessageIdRef.current === lastReadMessageId
      ) {
        console.log(
          "[ReadReceipt] Stable Debounce - already pending",
          lastReadMessageId,
        );
        return;
      }

      // 3. New target message? Cancel old timer and start new one.
      if (markReadTimeoutRef.current) {
        console.log("[ReadReceipt] Debounce - cancelling previous timer");
        clearTimeout(markReadTimeoutRef.current);
      }

      pendingReadMessageIdRef.current = lastReadMessageId;
      console.log(
        `[ReadReceipt] Scheduling markAsRead for: ${lastReadMessageId} (MyId: ${currentUserId})`,
      );

      markReadTimeoutRef.current = setTimeout(() => {
        console.log(
          `[ReadReceipt] Marking room ${roomId} as read up to ${lastReadMessageId}`,
        );
        markRoomAsReadMutation(
          { roomId, data: { lastReadMessageId } },
          {
            onSuccess: () => {
              lastMarkedMessageIdRef.current = lastReadMessageId;
              pendingReadMessageIdRef.current = null; // Clear pending state
              markReadTimeoutRef.current = null;
            },
            onError: () => {
              // On error, we clear pending so we can try again later
              pendingReadMessageIdRef.current = null;
              markReadTimeoutRef.current = null;
            },
          },
        );
      }, 100);
    },
    [roomId, currentUserId, messages, markRoomAsReadMutation],
  );

  return { markMessagesAsRead };
}
